import { Box, Typography, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import React, { FC } from "react";

import { StrengthTags } from "@/components/agent/PlannerTags";
import { PLANNER_STRENGTHS_LABEL } from "@/constants/plannerProfile";
import theme from "@/theme";

const useStyles = makeStyles(() => ({
  root: {
    padding: theme.spacing(2, 2, 2.5),
    borderBottom: `1px solid ${theme.palette.black[200]}`,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    cursor: "pointer",
    // 選択中カードの背景がホバーで上書きされないよう、未選択時だけホバー色を付ける
    "&:not($rootSelected):hover": {
      backgroundColor: theme.palette.blue[50],
    },
  },
  // 選択中は背景と枠線の色で強調する（プランナー一覧の選択中カードと同じ表現）。
  // 行はフラットな一覧のため、レイアウトが1pxずれないよう枠線は内側のアウトラインで描く
  rootSelected: {
    backgroundColor: theme.palette.black[100],
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  badges: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  badge: {
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    color: theme.palette.common.white,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  badgeToday: {
    backgroundColor: theme.palette.primary.main,
  },
  badgeWeekly: {
    backgroundColor: theme.palette.blue[500],
  },
  badgeUnread: {
    backgroundColor: theme.palette.warning.main,
  },
  date: {
    marginLeft: "auto",
    color: theme.palette.black[600],
    whiteSpace: "nowrap",
  },
  body: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "flex-start",
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    objectFit: "cover",
    border: `1px solid ${theme.palette.black[300]}`,
    flexShrink: 0,
  },
  avatarPlaceholder: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    backgroundColor: theme.palette.black[200],
    flexShrink: 0,
  },
  texts: {
    minWidth: 0,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  nameUnread: {
    fontWeight: 700,
  },
  message: {
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
  messageUnread: {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  tagsLabel: {
    fontWeight: 500,
  },
}));

interface ProposalListCardProps {
  profileImagePath: string;
  representativeName: string;
  companyName: string;
  message?: string;
  proposalDate?: string;
  isUnread?: boolean;
  // ホームの新規通知ではバッジを出さず太字表示のみ使うため分離している
  showUnreadBadge?: boolean;
  strengthLabels: string[];
  selected: boolean;
  onClick: () => void;
}

// メッセージ画面（PC）の左ペインに表示するコンパクトなオファーカード
export const ProposalListCard: FC<ProposalListCardProps> = ({
  profileImagePath,
  representativeName,
  companyName,
  message,
  proposalDate,
  isUnread = false,
  showUnreadBadge = true,
  strengthLabels,
  selected,
  onClick,
}) => {
  const classes = useStyles();

  const parsedDate = proposalDate ? dayjs(proposalDate) : null;
  const today = dayjs().startOf("day");
  const isToday = Boolean(parsedDate?.isSame(today, "day"));
  const isWithinAWeek =
    Boolean(parsedDate) &&
    !isToday &&
    today.diff(parsedDate.startOf("day"), "day") >= 0 &&
    today.diff(parsedDate.startOf("day"), "day") <= 6;

  return (
    <Box className={`${classes.root} ${selected ? classes.rootSelected : ""}`} onClick={onClick} role="button">
      <Box className={classes.headerRow}>
        <Box className={classes.badges}>
          {isToday && (
            <Typography variant="caption" className={`${classes.badge} ${classes.badgeToday}`}>
              本日届いたオファー
            </Typography>
          )}
          {isWithinAWeek && (
            <Typography variant="caption" className={`${classes.badge} ${classes.badgeWeekly}`}>
              1週間以内のオファー
            </Typography>
          )}
          {isUnread && showUnreadBadge && (
            <Typography variant="caption" className={`${classes.badge} ${classes.badgeUnread}`}>
              未読
            </Typography>
          )}
        </Box>
        <Typography variant="caption" className={classes.date}>
          {parsedDate ? parsedDate.format("M月D日") : ""}
        </Typography>
      </Box>
      <Box className={classes.body}>
        {profileImagePath ? (
          <img src={profileImagePath} alt={representativeName} className={classes.avatar} />
        ) : (
          <Box className={classes.avatarPlaceholder} />
        )}
        <Box className={classes.texts}>
          <Typography variant="body2" className={isUnread ? classes.nameUnread : undefined}>
            {representativeName} / {companyName}
          </Typography>
          {message && (
            <Typography variant="caption" className={`${classes.message} ${isUnread ? classes.messageUnread : ""}`}>
              {message}
            </Typography>
          )}
        </Box>
      </Box>
      {strengthLabels.length > 0 && (
        <Box>
          <Typography variant="caption" className={classes.tagsLabel}>
            {PLANNER_STRENGTHS_LABEL}
          </Typography>
          <Box mt={0.5}>
            <StrengthTags labels={strengthLabels} />
          </Box>
        </Box>
      )}
    </Box>
  );
};
